import { Fade, Menu, MenuProps } from '@mui/material';
import { GridRowId } from '@mui/x-data-grid';
import React, { FC, ReactNode, useRef, useState } from 'react';

/**
 * Interface for the Menu component props
 * @extends Omit<MenuProps, 'open'>
 */
export interface MenuInterface extends Omit<MenuProps, 'open'> {
  children: ReactNode | ReactNode[];
  disableOnClose?: boolean;
}

/**
 * Interface for the useMenu hook return value
 */
export interface UseMenuInterface {
  MenuWrapper: React.FC<MenuInterface>;
  handleContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    cb?: (id: GridRowId | null | undefined) => void,
  ) => void;
  handleNoDataContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    data: string,
  ) => void;
  handleClose: (event?: React.MouseEvent<HTMLElement>) => void;
  selectedData: string | null | undefined;
  contextMenu: {
    mouseX: number;
    mouseY: number;
  } | null;
  removeSelectedData: () => void;
  manualSetSelectedData: (id: string) => void;
  handleTouchStart: (
    e: React.TouchEvent<HTMLElement>,
    cb?: (id: GridRowId | null | undefined) => void,
    data?: string,
  ) => void;
  handleTouchEnd: () => void;
}

/**
 * Custom hook for managing context menu functionality
 * @returns {UseMenuInterface} An object containing menu-related functions and state
 */
const useMenu = (): UseMenuInterface => {
  // Ref for holding timeout for long press detection
  const holdTimeout = useRef<NodeJS.Timeout | null>(null);

  // State for context menu position
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // State for selected data
  const [selectedData, setSelectedData] = useState<string | null | undefined>(
    '',
  );

  /**
   * Manually set the selected data
   * @param {string} id - The id to set as selected data
   */
  const manualSetSelectedData = (id: string) => setSelectedData(id);

  /**
   * Remove the selected data
   */
  const removeSelectedData = () => {
    setSelectedData(null);
  };

  /**
   * Handle context menu for data table
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event
   * @param {Function} cb - Optional callback function
   */
  const handleContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    cb?: (id: GridRowId | null | undefined) => void,
  ) => {
    event.preventDefault();
    const target = event.target as HTMLElement;

    const rowElement = target.closest('.MuiDataGrid-row');
    const id = rowElement?.getAttribute('data-id');
    setSelectedData(id);
    cb?.(id);

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  /**
   * Handle touch start event for long press detection
   * @param {React.TouchEvent<HTMLElement>} e - The touch event
   * @param {Function} cb - Optional callback function
   * @param {string} data - Optional data to be set
   */
  const handleTouchStart = (
    e: React.TouchEvent<HTMLElement>,
    cb?: (id: GridRowId | null | undefined) => void,
    data?: string,
  ) => {
    // Start a timeout for detecting a long press (e.g., 500ms)
    holdTimeout.current = setTimeout(() => {
      const target = e.target as HTMLElement;

      const rowElement = target.closest('.MuiDataGrid-row');
      const id = rowElement?.getAttribute('data-id');
      setSelectedData(id || data);
      cb?.(id || data);

      // Touch event handling
      const touch = e.touches[0];
      setContextMenu(
        contextMenu === null
          ? {
              mouseX: touch.clientX + 2,
              mouseY: touch.clientY - 6,
            }
          : null,
      );
    }, 500);
  };

  /**
   * Handle touch end event to clear the long press timeout
   */
  const handleTouchEnd = () => {
    // Clear the timeout if the touch is released before the long press is detected
    if (holdTimeout.current) {
      clearTimeout(holdTimeout.current);
      holdTimeout.current = null;
    }
  };

  /**
   * Handle context menu for non-data table usage
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event
   * @param {string} data - The data to be set
   */
  const handleNoDataContextMenu = (
    event: React.MouseEvent<HTMLElement>,
    data: string,
  ) => {
    event.preventDefault();
    setSelectedData(data);
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  /**
   * Handle closing of the context menu
   * @param {React.MouseEvent<HTMLElement>} event - Optional mouse event
   */
  const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    setContextMenu(null);
  };

  /**
   * Wrapper component for the Menu
   * @param {MenuInterface} props - The props for the Menu component
   * @returns {React.ReactElement} The Menu component
   */
  const MenuWrapper: FC<MenuInterface> = ({
    children,
    disableOnClose,
    ...props
  }) => {
    const menuItems = React.Children.toArray(children);
    return (
      <Menu
        id='use-menu'
        open={contextMenu !== null}
        onClose={disableOnClose ? () => {} : () => handleClose()}
        TransitionComponent={Fade}
        anchorReference='anchorPosition'
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        sx={{
          '.MuiList-padding': {
            p: 0,
            minWidth: '178px',
          },
          '& li': {
            height: '40px',
          },
          borderRadius: '4px',
        }}
        onContextMenu={handleClose}
        {...props}
      >
        {menuItems}
      </Menu>
    );
  };

  return {
    MenuWrapper,
    handleContextMenu,
    handleNoDataContextMenu,
    handleClose,
    selectedData,
    contextMenu,
    removeSelectedData,
    manualSetSelectedData,
    handleTouchStart,
    handleTouchEnd,
  };
};

export default useMenu;
